import { Button, IconButton, Input, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Typography, CircularProgress } from "@mui/material";
import { DishForm } from "components/DishForm";
import { DishGroupForm } from "components/DishGroupForm";
import { useStateContext } from "contexts/userContext";
import { getItems, getGroups, postGroup, putItem, putGroup } from "helpers/api";
import { getInitialTranslations } from "helpers/translations";
import { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaPencilAlt, FaPlus, FaPlusCircle, FaDownload, FaUpload, FaFileExcel, FaLanguage, FaImage, FaSave, FaGripVertical } from "react-icons/fa";
import { exportMenuToExcel, importMenuFromExcel, generateFruitsVegetablesTemplate } from "utils/excelUtils";
import axios from "axios";
import { baseURL } from "constants/api";
import { getTranslationsForText } from "utils/translationUtils";
import useDishImageGeneration from "hooks/useDishImageGeneration";
import useMenuParser from "hooks/useMenuParser";
import GenerationLimitDialog from "components/GenerationLimitDialog";
import { cropImageByBox } from "utils/cropImage";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Специальный ID для группы "Other" (блюда без группы)
const OTHER_GROUP_ID = "other";

// Sortable group item component
function SortableGroupItem({ group, currentGroupId, language, onTypeClick, onItemGroupEdit, t }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "#f0f9ff" : "transparent",
    borderRadius: "8px",
    padding: "4px",
    border: isDragging ? "2px dashed #2196f3" : "2px dashed transparent",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex-row g10 flex-y-center nowrap"
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "4px 8px",
          color: "#999",
          transition: "all 0.2s ease",
          cursor: isDragging ? "grabbing" : "grab",
          borderRadius: "4px",
          touchAction: "none",
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.color = "#2196f3";
            e.currentTarget.style.backgroundColor = "#f5f5f5";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "#999";
          e.currentTarget.style.backgroundColor = "transparent";
        }}
        title={t("Drag to reorder")}
      >
        <FaGripVertical size={16} />
      </div>

      <Button
        data-id={group.id}
        variant={currentGroupId === group.id ? "contained" : "outlined"}
        onClick={onTypeClick}
      >
        {group.translations?.[language]?.trim()
          ? group.translations[language]
          : group.name?.trim() || ""}
      </Button>

      <IconButton onClick={onItemGroupEdit} data-id={group.id} size="small">
        <FaPencilAlt />
      </IconButton>
    </div>
  );
}

export default function DishesNew() {
  const { restaurant_id, language } = useStateContext();

  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);

  const [currentGroupId, setCurrentGroup] = useState(null);

  const [itemForEdit, setItemForEdit] = useState(null);
  const [editedItemGroup, setEditedItemGroup] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const importFileInputRef = useRef(null);
  const photoMenuInputRef = useRef(null);
  const [bulkTranslationLoading, setBulkTranslationLoading] = useState(false);
  const [bulkImageLoading, setBulkImageLoading] = useState(false);
  const [progressDialog, setProgressDialog] = useState({
    open: false,
    title: "",
    current: 0,
    total: 0,
    currentItem: "",
  });
  const cancelProcessRef = useRef(false);
  // Синхронная копия limitReached для чтения внутри цикла генерации
  const limitReachedRef = useRef(false);
  const [groupsOrderChanged, setGroupsOrderChanged] = useState(false);
  const [savingGroupsOrder, setSavingGroupsOrder] = useState(false);

  const { t } = useTranslation();
  // Генерация фото блюд через бэкенд (OpenAI + серверный счётчик/лимит бесплатной версии)
  const { generateImage, limitReached, setLimitReached } = useDishImageGeneration();
  // Распознавание меню по фото (тот же бэкенд /menu/parse, что и на стартовой странице)
  const { parseMenu, loading: photoMenuLoading } = useMenuParser();

  // Держим синхронную копию флага лимита для чтения внутри цикла генерации
  useEffect(() => {
    limitReachedRef.current = limitReached;
  }, [limitReached]);

  // Setup sensors for @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!restaurant_id) return;

    setItemsLoading(true);
    getItems(restaurant_id)
      .then(setItems)
      .finally(() => setItemsLoading(false));

    getGroups(restaurant_id).then((data) => {
      // Sort groups by order field
      const sortedGroups = data.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Добавляем специальную группу "Other" для блюд без группы
      const otherGroup = {
        id: OTHER_GROUP_ID,
        name: "Other",
        translations: {},
        order: 9999, // Помещаем в конец списка
      };

      const groupsWithOther = [...sortedGroups, otherGroup];
      setGroups(groupsWithOther);

      // Автоматически выбираем группу "Other", если нет других групп
      if (sortedGroups.length === 0) {
        setCurrentGroup(OTHER_GROUP_ID);
      } else if (sortedGroups[0]) {
        setCurrentGroup(sortedGroups[0].id);
      }
    });
  }, [restaurant_id]);

  const onTypeClick = (e) => {
    const id = e.target.dataset.id;
    // Если это "other", оставляем строкой, иначе преобразуем в число
    setCurrentGroup(id === OTHER_GROUP_ID ? id : +id);
  };

  const onItemGroupEdit = useCallback(
    (e) => {
      const id = e.currentTarget.dataset.id;

      // Запрещаем редактировать группу "Other"
      if (id === OTHER_GROUP_ID) {
        return;
      }

      const numericId = +id;
      if (!numericId) {
        console.error("onItemGroupEdit dataset.id not found");
        return;
      }

      const data = groups.find((item) => item.id === numericId);
      if (!data) {
        console.error("onItemGroupEdit itemData not found");
        return;
      }

      setEditedItemGroup(data);
    },
    [groups]
  );

  const onItemGroupAdd = useCallback(() => {
    setEditedItemGroup({
      id: null,
      name: "",
      order: null,
      translations: getInitialTranslations(),
    });
  }, []);

  const onItemEdit = useCallback(
    (e) => {
      const id = +e.currentTarget.dataset.id;
      if (!id) {
        console.error("onItemEdit dataset.id not found");
        return;
      }

      const itemData = items.find((item) => item.id === id);
      if (!itemData) {
        console.error("onItemEdit itemData not found");
        return;
      }

      setItemForEdit(itemData);
    },
    [items]
  );

  const onItemAdd = useCallback(() => {
    const currentGroupData = groups.find((group) => group.id === currentGroupId);

    const group_obj = currentGroupData
      ? {
          id: currentGroupData.id,
          name: currentGroupData.name,
        }
      : null;

    const newData = {
      id: null,
      title: "",
      description: "",
      price: null,
      toppings: [],
      extras: [],
      image: "",
      group_obj,
      translations: getInitialTranslations(),
      translations_descriptions: getInitialTranslations(),
    };

    setItemForEdit(newData);
  }, [currentGroupId, groups]);

  const onItemFormSuccess = useCallback((itemData) => {
    setItemForEdit(null);

    if (!itemData.id) return;

    setItems((currentItems) => {
      const newItems = [...currentItems];

      const editedItemIndex = currentItems.findIndex((item) => item.id === itemData.id);
      if (editedItemIndex === -1) {
        newItems.push(itemData);
      } else {
        newItems[editedItemIndex] = itemData;
      }

      return newItems;
    });
  }, []);

  const onItemGroupFormSuccess = useCallback((data) => {
    setEditedItemGroup(null);

    if (!data.id) return;

    setGroups((currentItems) => {
      const newItems = [...currentItems];

      const editedItemIndex = currentItems.findIndex((item) => item.id === data.id);
      if (editedItemIndex === -1) {
        newItems.push(data);
      } else {
        newItems[editedItemIndex] = data;
      }

      // Sort groups by order field (excluding "Other" group)
      const otherGroup = newItems.find(item => item.id === OTHER_GROUP_ID);
      const regularGroups = newItems.filter(item => item.id !== OTHER_GROUP_ID);
      const sortedRegularGroups = regularGroups.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      return otherGroup ? [...sortedRegularGroups, otherGroup] : sortedRegularGroups;
    });
  }, []);

  const onItemFormClose = () => {
    setItemForEdit(null);
  };

  const onItemDelete = useCallback((id) => {
    setItems((currentItems) => {
      const deletedItemIndex = currentItems.findIndex((item) => item.id === id);
      if (deletedItemIndex === -1) return;

      const newItems = [...currentItems];
      newItems.splice(deletedItemIndex, 1);

      return newItems;
    });

    setItemForEdit(null);
  }, []);

  const onItemGroupDelete = useCallback(
    (id) => {
      // Запрещаем удалять группу "Other"
      if (id === OTHER_GROUP_ID) {
        return;
      }

      setGroups((currentItems) => {
        const deletedIndex = currentItems.findIndex((item) => item.id === id);
        if (deletedIndex === -1) return;

        const newItems = [...currentItems];
        newItems.splice(deletedIndex, 1);

        return newItems;
      });

      if (currentGroupId === id) {
        // Find regular groups (excluding "Other")
        const regularGroups = groups.filter(g => g.id !== OTHER_GROUP_ID && g.id !== id);
        
        // If no regular groups left, select "Other", otherwise select first regular group
        if (regularGroups.length === 0) {
          setCurrentGroup(OTHER_GROUP_ID);
        } else {
          setCurrentGroup(regularGroups[0].id);
        }
      }

      setEditedItemGroup(null);
    },
    [groups, currentGroupId]
  );

  // Export menu to Excel
  const handleExportMenu = () => {
    try {
      exportMenuToExcel(items, groups, `menu-${restaurant_id}-${Date.now()}.xlsx`);
      setSnackbar({ open: true, message: t("Menu exported successfully!"), severity: "success" });
    } catch (error) {
      console.error("Error exporting menu:", error);
      setSnackbar({ open: true, message: t("Error exporting menu. Please try again."), severity: "error" });
    }
  };

  // Download template
  const handleDownloadTemplate = () => {
    try {
      generateFruitsVegetablesTemplate();
      setSnackbar({ open: true, message: t("Template downloaded successfully!"), severity: "success" });
    } catch (error) {
      console.error("Error downloading template:", error);
      setSnackbar({ open: true, message: t("Error downloading template. Please try again."), severity: "error" });
    }
  };

  // Import menu from Excel
  const handleImportMenu = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      // Import data from Excel file
      const importedData = await importMenuFromExcel(file);
      console.log("Imported data from Excel:", importedData);

      const stats = {
        groups: { created: 0, existing: 0 },
        items: { created: 0, failed: 0 },
      };

      // Maps to store created/existing items by name
      const groupsMap = new Map();

      // 1. Create or find Groups
      for (const groupData of importedData.groups) {
        const existingGroup = groups.find(g => g.name === groupData.name);

        if (existingGroup) {
          groupsMap.set(groupData.name, existingGroup);
          stats.groups.existing++;
        } else {
          try {
            const newGroup = await postGroup({
              ...groupData,
              restaurant_id: restaurant_id,
              translations: getInitialTranslations(),
            });
            groupsMap.set(groupData.name, newGroup);
            stats.groups.created++;
          } catch (error) {
            console.error("Error creating group:", error);
          }
        }
      }

      // 2. Create Items with proper references
      for (const itemData of importedData.items) {
        const group = groupsMap.get(itemData.group_name);

        const itemToAdd = {
          title: itemData.title,
          description: itemData.description,
          price: itemData.price,
          image: itemData.image,
          restaurant_id: restaurant_id,
          group_obj: group || null,
          toppings: [],
          extras: [],
          translations: getInitialTranslations(),
          translations_descriptions: getInitialTranslations(),
        };

        try {
          await axios.post(`${baseURL}/dishes/`, itemToAdd);
          stats.items.created++;
        } catch (error) {
          console.error("Error creating item:", error);
          stats.items.failed++;
        }
      }

      // 3. Refresh all data
      await new Promise(resolve => setTimeout(resolve, 500));

      const [updatedItems, updatedGroups] = await Promise.all([
        getItems(restaurant_id),
        getGroups(restaurant_id),
      ]);

      setItems(updatedItems);

      // Sort groups by order field
      const sortedGroups = updatedGroups.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Добавляем специальную группу "Other" для блюд без группы
      const otherGroup = {
        id: OTHER_GROUP_ID,
        name: "Other",
        translations: {},
        order: 9999,
      };
      const groupsWithOther = [...sortedGroups, otherGroup];
      setGroups(groupsWithOther);

      // Set current group: if no groups exist, select "Other", otherwise select first group
      if (!currentGroupId) {
        if (sortedGroups.length === 0) {
          setCurrentGroup(OTHER_GROUP_ID);
        } else {
          setCurrentGroup(sortedGroups[0].id);
        }
      }

      // Show success message
      const messages = [];
      if (stats.groups.created > 0) messages.push(`${stats.groups.created} groups created`);
      if (stats.items.created > 0) messages.push(`${stats.items.created} items created`);

      const successMessage = messages.length > 0
        ? `Successfully imported: ${messages.join(', ')}!`
        : 'Import completed!';

      setSnackbar({
        open: true,
        message: t(successMessage),
        severity: "success",
      });

      setLoading(false);

      // Reset file input
      if (importFileInputRef.current) {
        importFileInputRef.current.value = "";
      }
    } catch (error) {
      setLoading(false);
      console.error("Error importing menu:", error);
      setSnackbar({
        open: true,
        message: t(`Error importing menu: ${error.message}`),
        severity: "error",
      });
    }
  };

  // Импорт меню по фото: распознаём блюда и ДОБАВЛЯЕМ их к существующим (не заменяя).
  const handleImportFromPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    const objectUrl = URL.createObjectURL(file);

    try {
      const dishes = await parseMenu(file);

      if (!dishes || dishes.length === 0) {
        setSnackbar({
          open: true,
          message: t("No dishes recognized on the photo. Try a clearer menu photo."),
          severity: "warning",
        });
        return;
      }

      let created = 0;
      let failed = 0;

      for (const dish of dishes) {
        // Если у блюда на фото есть картинка (box) — вырезаем фрагмент
        let image = "";
        if (dish.box) {
          const cropped = await cropImageByBox(objectUrl, dish.box);
          if (cropped) image = cropped;
        }

        try {
          await axios.post(`${baseURL}/dishes/`, {
            title: dish.title || "",
            description: dish.description || "",
            price: dish.price || 0,
            image,
            restaurant_id: restaurant_id,
            group_obj: null, // новые блюда попадают в группу "Other"
            toppings: [],
            extras: [],
            translations: getInitialTranslations(),
            translations_descriptions: getInitialTranslations(),
          });
          created++;
        } catch (error) {
          console.error("Error creating dish from photo:", error);
          failed++;
        }
      }

      // Обновляем список блюд (добавленные появятся рядом с существующими)
      const updatedItems = await getItems(restaurant_id);
      setItems(updatedItems);

      setSnackbar({
        open: true,
        message: t("Added {{count}} dishes from the photo", { count: created })
          + (failed > 0 ? ` (${failed} ${t("failed")})` : ""),
        severity: failed > 0 ? "warning" : "success",
      });
    } catch (error) {
      console.error("Error importing menu from photo:", error);
      setSnackbar({
        open: true,
        message: t("Error recognizing menu from photo"),
        severity: "error",
      });
    } finally {
      setLoading(false);
      URL.revokeObjectURL(objectUrl);
      if (photoMenuInputRef.current) photoMenuInputRef.current.value = "";
    }
  };

  // Bulk translate all items
  const handleBulkTranslate = async () => {
    if (items.length === 0) {
      setSnackbar({ open: true, message: t("No items to translate"), severity: "warning" });
      return;
    }

    cancelProcessRef.current = false;
    setBulkTranslationLoading(true);
    setProgressDialog({
      open: true,
      title: t("Translating Items"),
      current: 0,
      total: items.length,
      currentItem: "",
    });

    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < items.length; i++) {
        if (cancelProcessRef.current) {
          setSnackbar({
            open: true,
            message: t("Translation process cancelled"),
            severity: "info",
          });
          break;
        }

        const item = items[i];
        const itemTitle = (item.translations?.[language]?.trim() ? item.translations[language] : item.title) || t("Item") + ` ${i + 1}`;

        setProgressDialog(prev => ({
          ...prev,
          current: i + 1,
          currentItem: itemTitle,
        }));

        try {
          // Translate title if not already translated
          let updatedTranslations = { ...item.translations };
          if (item.title && (!item.translations || Object.values(item.translations).every(val => !val))) {
            const titleTranslations = await getTranslationsForText(item.title);
            updatedTranslations = titleTranslations;
          }

          // Translate description if not already translated
          let updatedDescriptionTranslations = { ...item.translations_descriptions };
          if (item.description && (!item.translations_descriptions || Object.values(item.translations_descriptions).every(val => !val))) {
            const descriptionTranslations = await getTranslationsForText(item.description);
            updatedDescriptionTranslations = descriptionTranslations;
          }

          // Update item with translations
          await putItem(item.id, {
            ...item,
            translations: updatedTranslations,
            translations_descriptions: updatedDescriptionTranslations,
          });

          successCount++;
        } catch (error) {
          console.error(`Error translating item ${item.id}:`, error);
          errorCount++;
        }
      }

      // Refresh items if any were processed
      if (successCount > 0) {
        const updatedItems = await getItems(restaurant_id);
        setItems(updatedItems);
      }

      if (!cancelProcessRef.current) {
        setSnackbar({
          open: true,
          message: t("Translations completed: {{success}} successful, {{failed}} failed", { success: successCount, failed: errorCount }),
          severity: errorCount > 0 ? "warning" : "success",
        });
      }
    } catch (error) {
      console.error("Error during bulk translation:", error);
      setSnackbar({ open: true, message: t("Error during bulk translation"), severity: "error" });
    } finally {
      cancelProcessRef.current = false;
      setBulkTranslationLoading(false);
      setProgressDialog(prev => ({ ...prev, open: false }));
    }
  };

  // Bulk generate images for items without images
  const handleBulkGenerateImages = async () => {
    const itemsWithoutImages = items.filter(item => !item.image || item.image === "");

    if (itemsWithoutImages.length === 0) {
      setSnackbar({ open: true, message: t("All items already have images"), severity: "info" });
      return;
    }

    cancelProcessRef.current = false;
    limitReachedRef.current = false;
    setLimitReached(false);
    setBulkImageLoading(true);
    setProgressDialog({
      open: true,
      title: t("Generating Images"),
      current: 0,
      total: itemsWithoutImages.length,
      currentItem: "",
    });

    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < itemsWithoutImages.length; i++) {
        if (cancelProcessRef.current) {
          setSnackbar({
            open: true,
            message: t("Image generation process cancelled"),
            severity: "info",
          });
          break;
        }

        const item = itemsWithoutImages[i];
        const title = (item.translations?.[language]?.trim() ? item.translations[language] : item.title?.trim()) || t("Item") + ` ${i + 1}`;

        setProgressDialog(prev => ({
          ...prev,
          current: i + 1,
          currentItem: title,
        }));

        try {
          const description = item.translations_descriptions?.[language]?.trim()
            ? item.translations_descriptions[language]
            : item.description?.trim() || "";

          // Генерация на бэкенде: prompt формируется на сервере, ведётся счётчик и лимит
          const generatedImage = await generateImage(restaurant_id, title, description);

          // Упёрлись в лимит бесплатной версии — прекращаем процесс, окно покажется через limitReached
          if (cancelProcessRef.current === false && generatedImage === null && limitReachedRef.current) {
            break;
          }

          if (generatedImage) {
            await putItem(item.id, {
              ...item,
              image: generatedImage,
            });
            successCount++;
          } else {
            errorCount++;
          }

          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.error(`Error generating image for item ${item.id}:`, error);
          errorCount++;
        }
      }

      // Refresh items if any were processed
      if (successCount > 0) {
        const updatedItems = await getItems(restaurant_id);
        setItems(updatedItems);
      }

      if (!cancelProcessRef.current) {
        setSnackbar({
          open: true,
          message: t("Image generation completed: {{success}} successful, {{failed}} failed", { success: successCount, failed: errorCount }),
          severity: errorCount > 0 ? "warning" : "success",
        });
      }
    } catch (error) {
      console.error("Error during bulk image generation:", error);
      setSnackbar({ open: true, message: t("Error during bulk image generation"), severity: "error" });
    } finally {
      cancelProcessRef.current = false;
      setBulkImageLoading(false);
      setProgressDialog(prev => ({ ...prev, open: false }));
    }
  };

  const handleCancelProcess = () => {
    cancelProcessRef.current = true;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle drag and drop for groups with @dnd-kit
  const handleGroupDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setGroups((items) => {
      // Filter out "Other" group for reordering
      const regularGroups = items.filter(g => g.id !== OTHER_GROUP_ID);
      const otherGroup = items.find(g => g.id === OTHER_GROUP_ID);

      const oldIndex = regularGroups.findIndex(g => g.id === active.id);
      const newIndex = regularGroups.findIndex(g => g.id === over.id);

      const reorderedGroups = arrayMove(regularGroups, oldIndex, newIndex);

      // Add "Other" group back at the end
      const finalGroups = otherGroup ? [...reorderedGroups, otherGroup] : reorderedGroups;
      
      setGroupsOrderChanged(true);
      return finalGroups;
    });
  };

  // Save groups order
  const handleSaveGroupsOrder = async () => {
    setSavingGroupsOrder(true);
    try {
      const regularGroups = groups.filter(g => g.id !== OTHER_GROUP_ID);
      
      for (let i = 0; i < regularGroups.length; i++) {
        const group = regularGroups[i];
        await putGroup({
          ...group,
          restaurant_id: restaurant_id,
          order: i + 1,
        });
      }

      setSnackbar({
        open: true,
        message: t("Groups order saved successfully!"),
        severity: "success",
      });
      setGroupsOrderChanged(false);

      // Refresh groups with proper sorting
      const updatedGroups = await getGroups(restaurant_id);
      const sortedGroups = updatedGroups.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      const otherGroup = {
        id: OTHER_GROUP_ID,
        name: "Other",
        translations: {},
        order: 9999,
      };
      const groupsWithOther = [...sortedGroups, otherGroup];
      setGroups(groupsWithOther);
    } catch (error) {
      console.error("Error saving groups order:", error);
      setSnackbar({
        open: true,
        message: t("Error saving groups order"),
        severity: "error",
      });
    } finally {
      setSavingGroupsOrder(false);
    }
  };

  return (
    <>
      <div className="flex-column g10 p10">
        {/* Action buttons container */}
        <div className="flex-column" style={{ gap: "20px", marginBottom: "20px" }}>
          {/* 1. Import/Export Menu Section */}
          <div style={{
            padding: "15px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            border: "2px solid #e0e0e0"
          }}>
            <Typography variant="subtitle2" style={{ marginBottom: "10px", fontWeight: "600", color: "#555" }}>
              {t("Menu Import/Export")}
            </Typography>
            <div className="flex-row" style={{ justifyContent: "flex-start", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <div className="nowrap">
                <Button startIcon={<FaFileExcel size={12} />} variant="outlined" color="primary" onClick={handleDownloadTemplate}>
                  {t("Download Template")}
                </Button>
              </div>

              <div className="nowrap">
                <label htmlFor="import-menu-file-new">
                  <Input
                    id="import-menu-file-new"
                    type="file"
                    inputProps={{
                      accept: ".xlsx, .xls",
                      style: { display: "none" },
                    }}
                    inputRef={importFileInputRef}
                    onChange={handleImportMenu}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    component="span"
                    startIcon={<FaUpload size={12} />}
                    disabled={loading}
                  >
                    {loading ? t("Importing...") : t("Import Menu")}
                  </Button>
                </label>
              </div>

              <div className="nowrap">
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<FaDownload size={12} />}
                  onClick={handleExportMenu}
                  disabled={items.length === 0}
                >
                  {t("Export Menu")}
                </Button>
              </div>

              <div className="nowrap">
                <label htmlFor="import-menu-photo-new">
                  <Input
                    id="import-menu-photo-new"
                    type="file"
                    inputProps={{
                      accept: "image/*",
                      style: { display: "none" },
                    }}
                    inputRef={photoMenuInputRef}
                    onChange={handleImportFromPhoto}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    component="span"
                    startIcon={<FaImage size={12} />}
                    disabled={loading || photoMenuLoading}
                  >
                    {photoMenuLoading ? t("Recognizing...") : t("Menu from photo")}
                  </Button>
                </label>
              </div>
            </div>
          </div>

          {/* 2. Auto-generation Section */}
          <div style={{
            padding: "15px",
            backgroundColor: "#fff3e0",
            borderRadius: "8px",
            border: "2px solid #ffe0b2"
          }}>
            <Typography variant="subtitle2" style={{ marginBottom: "10px", fontWeight: "600", color: "#555" }}>
              {t("Auto-generation Tools")}
            </Typography>
            <div className="flex-row" style={{ justifyContent: "flex-start", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <div className="nowrap">
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<FaLanguage size={12} />}
                  onClick={handleBulkTranslate}
                  disabled={bulkTranslationLoading || items.length === 0}
                >
                  {bulkTranslationLoading ? t("Translating...") : t("Translate All Items")}
                </Button>
              </div>

              <div className="nowrap">
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<FaImage size={12} />}
                  onClick={handleBulkGenerateImages}
                  disabled={bulkImageLoading || items.length === 0}
                >
                  {bulkImageLoading ? t("Generating...") : t("Generate Missing Images")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Groups Section */}
        <div style={{
          padding: "15px",
          backgroundColor: "#e3f2fd",
          borderRadius: "8px",
          border: "2px solid #bbdefb",
          marginBottom: "15px"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography variant="subtitle2" style={{ fontWeight: "600", color: "#555" }}>
                {t("Menu Groups")}
              </Typography>
              <Typography variant="caption" style={{ color: "#888", display: "flex", alignItems: "center", gap: "4px" }}>
                <FaGripVertical size={10} style={{ color: "#999" }} />
                {t("Drag to reorder")}
              </Typography>
            </div>
            {groupsOrderChanged && (
              <Button
                startIcon={<FaSave size={12} />}
                variant="contained"
                color="success"
                size="small"
                onClick={handleSaveGroupsOrder}
                disabled={savingGroupsOrder}
              >
                {savingGroupsOrder ? t("Saving...") : t("Save Order")}
              </Button>
            )}
          </div>
          
          {/* Regular groups - first row with drag and drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleGroupDragEnd}
          >
            <div className="flex-row g10" style={{ paddingBottom: "5px", flexWrap: "wrap" }}>
              <div className="nowrap">
                <Button startIcon={<FaPlus size={12} />} variant="outlined" onClick={onItemGroupAdd}>
                  {t("Add group")}
                </Button>
              </div>

              {console.log("groups", groups)}
              {console.log("language", language)}
              
              <SortableContext
                items={groups.filter(g => g.id !== OTHER_GROUP_ID).map(g => g.id)}
                strategy={horizontalListSortingStrategy}
              >
                {groups
                  .filter((group) => group.id !== OTHER_GROUP_ID)
                  .map((group) => (
                    <SortableGroupItem
                      key={group.id}
                      group={group}
                      currentGroupId={currentGroupId}
                      language={language}
                      onTypeClick={onTypeClick}
                      onItemGroupEdit={onItemGroupEdit}
                      t={t}
                    />
                  ))}
              </SortableContext>
            </div>
          </DndContext>

          {/* Other group button - second row */}
          <div className="flex-row g10" style={{ paddingTop: "10px" }}>
            {groups
              .filter((group) => group.id === OTHER_GROUP_ID)
              .map((group) => (
                <div className="flex-row g10 flex-y-center nowrap" key={group.id}>
                  <Button
                    data-id={group.id}
                    variant={currentGroupId === group.id ? "contained" : "outlined"}
                    onClick={onTypeClick}
                  >
                    {group.translations?.[language]?.trim()
                      ? group.translations[language]
                      : group.name?.trim() || ""}
                  </Button>
                </div>
              ))}
          </div>
        </div>

        {/* Items grid */}
        {itemsLoading ? (
          <div
            className="flex-column flex-y-center flex-x-center g10"
            style={{ padding: "60px 0", width: "100%" }}
          >
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              {t("Loading...")}
            </Typography>
          </div>
        ) : (
        <div className="flex-rows g10">
          <button
            className="border-light radius-default w200 center gray-bg-light-3"
            style={{ maxWidth: "calc(50% - 5px)" }}
            onClick={onItemAdd}
          >
            <span className="h160 flex-row flex-y-center flex-x-center">
              <FaPlusCircle size={50} className="gray-light" />
            </span>
            <span className="flex-column g10 p10">{t("Add new item")}</span>
          </button>
          {console.log("items", items)}{" "}
          {items
            .filter((item) => {
              // Если выбрана группа "Other", показываем блюда без группы
              if (currentGroupId === OTHER_GROUP_ID) {
                return !item.group_obj || item.group_obj.id === null;
              }
              // Иначе показываем блюда выбранной группы
              return item.group_obj?.id === currentGroupId;
            })
            .map((item) => (
              <div
                key={item.id}
                className="border-light radius-default w200 center ovh pos-relative"
                style={{ maxWidth: "calc(50% - 5px)", cursor: "pointer" }}
                data-id={item.id}
                onClick={onItemEdit}
              >
                {item.image ? (
                  <div
                    className="h160 bg-img-cover"
                    style={{ backgroundImage: `url(${item.image})` }}
                  ></div>
                ) : (
                  <div className="h160 flex-row flex-y-center flex-x-center gray-bg-light-3">
                    <FaImage size={50} className="gray-light" />
                  </div>
                )}

                <div className="flex-column g10 p10">
                  <div>
                    {item.translations?.[language]?.trim()
                      ? item.translations[language]
                      : item.title?.trim() || ""}
                  </div>
                </div>

                <div
                  className="pos-absolute bg-white radius-full"
                  style={{ right: "10px", top: "10px" }}
                >
                  <IconButton size="small" data-id={item.id} onClick={onItemEdit}>
                    <FaPencilAlt />
                  </IconButton>
                </div>
              </div>
            ))}
        </div>
        )}
      </div>

      {itemForEdit ? (
        <DishForm
          data={itemForEdit}
          restaurant_id={restaurant_id}
          groups={groups}
          onClose={onItemFormClose}
          onSuccess={onItemFormSuccess}
          onDelete={onItemDelete}
        />
      ) : null}

      {editedItemGroup ? (
        <DishGroupForm
          data={editedItemGroup}
          restaurant_id={restaurant_id}
          onClose={() => setEditedItemGroup(null)}
          onSuccess={onItemGroupFormSuccess}
          onDelete={onItemGroupDelete}
        />
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Progress Dialog */}
      <Dialog
        open={progressDialog.open}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>{progressDialog.title}</DialogTitle>
        <DialogContent>
          <div style={{ padding: "20px 0" }}>
            <Typography variant="body1" gutterBottom>
              {t("Processing")}: {progressDialog.current} / {progressDialog.total}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom style={{ marginBottom: "15px" }}>
              {progressDialog.currentItem}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progressDialog.current / progressDialog.total) * 100}
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Typography variant="caption" display="block" style={{ marginTop: "10px", textAlign: "center" }}>
              {Math.round((progressDialog.current / progressDialog.total) * 100)}%
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelProcess} color="error" variant="outlined">
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Окно лимита бесплатной версии: после 3 сгенерированных фото / при попытке генерации сверх лимита */}
      <GenerationLimitDialog open={limitReached} onClose={() => setLimitReached(false)} />
    </>
  );
}
